import React, { useState } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';

import { Plan } from 'api-bible-shared-lib/utils/types';
import { planCountLimitValidator } from 'api-bible-shared-lib/utils/validation';
import Modal from './Modal';
import { Alert, AlertType } from './Alert';
import { Button } from './Button';
import { InputCheckbox, InputGroup } from './Inputs';

import { usePlans } from '../contexts/Plans';
import { toLocaleDateTimeString } from '../utils/time';
import * as portalApi from '../utils/api';

interface PlanEditModalProps {
  planId: string;
  handleDismiss: () => void;
}

const validationSchema = yup.object().shape({
  name: yup.string().trim().required().label('Plan Name'),
  label: yup.string().trim().required().label('Plan Label'),
  color: yup.string().trim().required().label('Plan Color'),
  description: yup.string().trim().required().label('Plan Description'),
  features: yup.string().trim().required().label('Features'),
  dailyLimit: planCountLimitValidator.clone().required().label('Daily Limit'),
  monthlyLimit: planCountLimitValidator.clone().required().label('Monthly Limit'),
  monthlyBaseLimit: planCountLimitValidator.clone().label('Monthly Base Limit'),
});

export default function PlanEditModal({ planId, handleDismiss }: PlanEditModalProps) {
  const { plansById, reloadPlansById } = usePlans();
  const [error, setError] = useState<Error | undefined>();
  const [success, setSuccess] = useState<string | undefined>();

  const plan = plansById[planId];

  const onSubmit = async (values: Plan, { setSubmitting }: any) => {
    setSuccess(undefined);
    setError(undefined);

    try {
      await portalApi.putSuperAdminPlan(values);
      reloadPlansById();
      setSuccess('Successfully updated and reloaded.');
    } catch (e) {
      const err = e as Error;
      setError(err);
    }

    setSubmitting(false);
  };

  const initialValues: Plan = {
    id: plan.id,
    name: plan.name || '',
    label: plan.label,
    color: plan.color,
    description: plan.description,
    features: plan.features,
    dailyLimit: plan.dailyLimit,
    monthlyLimit: plan.monthlyLimit,
    monthlyBaseLimit: plan.monthlyBaseLimit,
    stripeProductId: plan.stripeProductId,
    sortOrder: plan.sortOrder,
    isUpcoming: plan.isUpcoming,
    isActive: plan.isActive,
    updatedAt: plan.updatedAt,
    createdAt: plan.createdAt,
  };

  const LineItem = ({
    label = '',
    span = '1',
    children,
  }: {
    label: string;
    children: React.ReactNode;
    span?: string;
  }) => (
    <div className={`sm:col-span-${span}`}>
      <dt className="text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-sm text-gray-900">{children}</dd>
    </div>
  );

  return (
    <Modal
      className="w-full md:max-w-screen-md xl:max-w-screen-lg"
      body={
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ handleSubmit, isSubmitting, values }) => (
            <form onSubmit={handleSubmit}>
              {success && (
                <Alert
                  className="mb-8"
                  type={AlertType.SUCCESS}
                  title="Success!"
                  message={success}
                />
              )}
              {error && (
                <Alert
                  className="mb-8"
                  type={AlertType.ERROR}
                  title="There was an error with your submission"
                  message={error?.message || 'An unexpected error occurred.'}
                />
              )}

              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Plan ID: {plan?.id}
                  </h3>
                </div>
                <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
                  <dl className="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-4">
                    <LineItem label="Plan Name">
                      <InputGroup name="name" />
                    </LineItem>
                    <LineItem label="Plan Label">
                      <InputGroup name="label" />
                    </LineItem>
                    <LineItem label="Plan Color">
                      <InputGroup name="color" />
                    </LineItem>
                    <LineItem label="Description">
                      <InputGroup name="description" placeholder="" />
                    </LineItem>
                    <LineItem label="Features Short List" span="4">
                      <InputGroup
                        name="features"
                        as="textarea"
                        rows="5"
                        placeholder="A carriage return delimited list"
                      />
                    </LineItem>
                    <LineItem label="Daily Limit">
                      <InputGroup name="dailyLimit" type="number" />
                    </LineItem>
                    <LineItem label="Monthly Limit">
                      <InputGroup name="monthlyLimit" type="number" />
                    </LineItem>
                    <LineItem label="Monthly Base Limit (optional)">
                      <InputGroup name="monthlyBaseLimit" type="number" />
                    </LineItem>
                    <LineItem label="Stripe Product ID (optional)">
                      <InputGroup name="stripeProductId" />
                    </LineItem>
                    <LineItem label="Sort Order">
                      <InputGroup name="sortOrder" type="number" />
                    </LineItem>
                    <LineItem label="">
                      <InputCheckbox
                        name="isUpcoming"
                        label="Upcoming"
                        checked={!!values.isUpcoming}
                        value={values.isUpcoming ? '1' : '0'}
                      />
                      <InputCheckbox
                        name="isActive"
                        label="Active"
                        checked={!!values.isActive}
                        value={values.isActive ? '1' : '0'}
                      />
                    </LineItem>
                    <LineItem label="Created">{toLocaleDateTimeString(plan?.createdAt)}</LineItem>
                    <LineItem label="Updated">{toLocaleDateTimeString(plan?.updatedAt)}</LineItem>
                  </dl>
                </div>
              </div>

              <div className="w-full pt-6 mt-8">
                <div className="flex justify-end">
                  <Button type="button" outline className="mr-2" onClick={handleDismiss}>
                    Dismiss
                  </Button>
                  <Button outline disabled={isSubmitting} loading={isSubmitting} type="submit">
                    Save Changes
                  </Button>
                </div>
              </div>
            </form>
          )}
        </Formik>
      }
    />
  );
}
